/**
 * Energy Role - Cast Pillar
 *
 * Normaliza input sujo para número tipado.
 * Detecta unidades em strings e converte se necessário.
 *
 * @example
 * import { castEnergy, castEnergyStrict, detectEnergyUnit } from '@attrx/role-morphic/energy/cast';
 *
 * castEnergy('100 kWh', 'kilowatt_hour');     // 100
 * castEnergy('100 BTU', 'joule');             // 105505.59 (converte)
 * castEnergyStrict('100 kWh');                // 100 (não converte)
 * detectEnergyUnit('100 kilocalories');       // 'kilocalorie'
 */

import { ENERGY_UNITS, ENERGY_ALIASES, type EnergyUnit } from "./constants";
import { convertEnergy } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, detectando e convertendo unidade se presente
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo para conversão
 * @returns Número ou null se não puder converter
 *
 * @example
 * castEnergy(100, 'joule');                   // 100
 * castEnergy('100', 'joule');                 // 100
 * castEnergy('100 kWh', 'kilowatt_hour');     // 100
 * castEnergy('100 BTU', 'joule');             // 105505.59 (converte)
 * castEnergy('1 kcal', 'kilojoule');          // 4.1868 (converte)
 * castEnergy('invalid', 'joule');             // null
 */
export function castEnergy(
  input: unknown,
  targetVariant: EnergyUnit = "joule"
): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseEnergyString(input, targetVariant);
  }

  return null;
}

/**
 * Faz cast sem conversão de unidade (só parseia o valor literal)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castEnergyStrict('100 kWh');   // 100 (ignora unidade, não converte)
 * castEnergyStrict('100');       // 100
 * castEnergyStrict(100);         // 100
 */
export function castEnergyStrict(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    const numValue = parseNumber(input);
    return numValue;
  }

  return null;
}

/**
 * Versão safe de castEnergy que retorna Result
 */
export function tryCastEnergy(
  input: unknown,
  targetVariant: EnergyUnit = "joule"
): Result<number> {
  const result = castEnergy(input, targetVariant);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to energy:${targetVariant}`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECTION
// =============================================================================

/**
 * Detecta unidade de energia a partir de uma string
 *
 * @param input - String com possível unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectEnergyUnit('100 kWh');           // 'kilowatt_hour'
 * detectEnergyUnit('100 kilocalories');  // 'kilocalorie'
 * detectEnergyUnit('100 BTU');           // 'btu'
 * detectEnergyUnit('100');               // null
 */
export function detectEnergyUnit(input: string): EnergyUnit | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);

  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  return detectUnit(unitStr);
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Parseia string para número, detectando unidade e convertendo se necessário
 */
function parseEnergyString(input: string, targetVariant: EnergyUnit): number | null {
  const trimmed = input.trim().toLowerCase();

  // Extrai número e possível unidade
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const [, numStr, unitStr] = match;

  // Normaliza número
  const numValue = parseNumber(numStr);
  if (numValue === null) {
    return null;
  }

  // Se tem unidade no input, converte se necessário
  const cleanUnitStr = unitStr.trim();
  if (cleanUnitStr) {
    const detectedUnit = detectUnit(cleanUnitStr);
    if (detectedUnit && detectedUnit !== targetVariant) {
      // Converte da unidade detectada para a target
      return convertEnergy(detectedUnit, targetVariant, numValue);
    }
  }

  return numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const afterComma = numStr.split(",").slice(1);
    const isThousandSep = afterComma.every((part) => part.length === 3);
    if (isThousandSep) {
      numStr = numStr.replace(/,/g, "");
    } else {
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos: múltiplos pontos = milhar brasileiro
    const afterDot = numStr.split(".").slice(1);
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    }
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}

/**
 * Detecta unidade a partir de string (símbolo ou alias)
 */
function detectUnit(unitStr: string): EnergyUnit | null {
  const normalized = unitStr.toLowerCase().trim();

  // Checa aliases primeiro
  if (normalized in ENERGY_ALIASES) {
    return ENERGY_ALIASES[normalized] as EnergyUnit;
  }

  // Checa símbolos e nomes
  for (const [variant, config] of Object.entries(ENERGY_UNITS)) {
    if (
      config.symbol.toLowerCase() === normalized ||
      config.singular?.toLowerCase() === normalized ||
      config.plural?.toLowerCase() === normalized ||
      variant.toLowerCase() === normalized
    ) {
      return variant as EnergyUnit;
    }
  }

  return null;
}
